import { useState, useEffect } from 'react';
import { Layout } from '../../../components/layout/Layout';
import api from '../../../api';
import { Input } from '../../../components/ui/input';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import {
  Search, HardDrive, Filter, Share2, Server,
  ChevronLeft, ChevronRight, X, ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

const formatSize = (gb) => {
  if (!gb) return '-';
  const n = Number(gb);
  if (n >= 1024) return `${(n / 1024).toFixed(1)} TB`;
  return `${n.toFixed(0)} GB`;
};

const HypervStorage = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ search: '', storage_type: '', usage_level: '' });
  const [typeOptions, setTypeOptions] = useState([]);
  const [sortConfig, setSortConfig] = useState({ field: 'used_pct', dir: 'desc' });
  const [activeStatFilter, setActiveStatFilter] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.storage_type) params.append('storage_type', filters.storage_type);
      if (filters.usage_level) params.append('usage_level', filters.usage_level);
      const res = await api.get(`/virt/hyperv/storage?${params}`);
      setItems(res.data.items);
      setPagination(prev => ({ ...prev, total: res.data.total, pages: res.data.pages }));
    } catch (err) {
      toast.error('Error al cargar storage Hyper-V');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => { try { setStats((await api.get('/virt/hyperv/storage/stats')).data); } catch (e) { console.error(e); } };
  const fetchTypes = async () => { try { setTypeOptions((await api.get('/virt/hyperv/storage/options/types')).data); } catch (e) { console.error(e); } };

  useEffect(() => { fetchStats(); fetchTypes(); }, []);
  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
  }, [pagination.page, filters, sortConfig]);

  const handleSort = (field) => setSortConfig(prev => ({ field, dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc' }));
  const SortIcon = ({ field }) => sortConfig.field !== field
    ? <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />
    : (sortConfig.dir === 'asc' ? <ChevronUp className="h-3 w-3 text-primary" /> : <ChevronDown className="h-3 w-3 text-primary" />);
  const SortableHeader = ({ field, children, className = '' }) => (
    <th className={`cursor-pointer hover:bg-muted/70 select-none ${className}`} onClick={() => handleSort(field)}>
      <div className="flex items-center gap-1">{children}<SortIcon field={field} /></div>
    </th>
  );

  const handleStatFilter = (t) => {
    setActiveStatFilter(t);
    setPagination(prev => ({ ...prev, page: 1 }));
    const map = { critical: 'critical', warning: 'warning', healthy: 'healthy' };
    setFilters({ search: '', storage_type: '', usage_level: map[t] || '' });
  };

  // Umbrales Hyper-V: 75% warning, 90% critical
  const getUsageColor = (pct) => {
    const p = Number(pct) || 0;
    if (p >= 90) return 'bg-red-500';
    if (p >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const clearFilters = () => { setFilters({ search: '', storage_type: '', usage_level: '' }); setActiveStatFilter('all'); };
  const hasFilters = filters.search || filters.storage_type || filters.usage_level;
  const getUsagePercent = () => (!stats || !stats.total_capacity_gb) ? 0 : ((stats.total_used_gb / stats.total_capacity_gb) * 100).toFixed(1);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="page-header">
          <h1 className="page-title">Storage (Hyper-V)</h1>
          <p className="page-subtitle">Volúmenes local / SMB / CSV (umbrales: 75% warning, 90% crítico)</p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card className={`card-subtle cursor-pointer transition-all hover:border-primary/50 ${activeStatFilter === 'all' ? 'border-primary ring-1 ring-primary/20' : ''}`} onClick={() => handleStatFilter('all')} data-testid="stat-total"><CardContent className="p-4 text-center"><p className="stats-card-value">{stats.total || 0}</p><p className="stats-card-label">Total</p></CardContent></Card>
            <Card className="card-subtle"><CardContent className="p-4 text-center"><p className="stats-card-value">{formatSize(stats.total_capacity_gb)}</p><p className="stats-card-label">Capacidad</p></CardContent></Card>
            <Card className="card-subtle"><CardContent className="p-4 text-center"><p className="stats-card-value">{formatSize(stats.total_used_gb)}</p><p className="stats-card-label">Usado ({getUsagePercent()}%)</p></CardContent></Card>
            <Card className="card-subtle"><CardContent className="p-4 text-center"><p className="stats-card-value text-green-500">{formatSize(stats.total_free_gb)}</p><p className="stats-card-label">Libre</p></CardContent></Card>
            <Card className={`card-subtle cursor-pointer transition-all hover:border-red-500/50 ${activeStatFilter === 'critical' ? 'border-red-500 ring-1 ring-red-500/20' : ''}`} onClick={() => handleStatFilter('critical')} data-testid="stat-critical"><CardContent className="p-4 text-center"><p className="stats-card-value text-red-500">{stats.critical_count || 0}</p><p className="stats-card-label">Críticos (≥90%)</p></CardContent></Card>
            <Card className={`card-subtle cursor-pointer transition-all hover:border-yellow-500/50 ${activeStatFilter === 'warning' ? 'border-yellow-500 ring-1 ring-yellow-500/20' : ''}`} onClick={() => handleStatFilter('warning')} data-testid="stat-warning"><CardContent className="p-4 text-center"><p className="stats-card-value text-yellow-500">{stats.warning_count || 0}</p><p className="stats-card-label">Warning (75-90%)</p></CardContent></Card>
          </div>
        )}

        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2"><Filter className="h-4 w-4 text-muted-foreground" /><span className="text-sm font-medium">Filtros:</span></div>
              <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-md">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input placeholder="Buscar volumen..." value={filters.search}
                  onChange={(e) => { setFilters(prev => ({ ...prev, search: e.target.value })); setActiveStatFilter(''); }}
                  className="h-9" data-testid="storage-search-input" />
              </div>
              {typeOptions.length > 0 && (
                <Select value={filters.storage_type || 'todos'} onValueChange={(v) => { setFilters(prev => ({ ...prev, storage_type: v === 'todos' ? '' : v })); setActiveStatFilter(''); }}>
                  <SelectTrigger className="w-36 h-9" data-testid="storage-type-filter"><SelectValue placeholder="Tipo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {typeOptions.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters}><X className="h-4 w-4 mr-1" /> Limpiar</Button>}
            </div>
          </CardContent>
        </Card>

        <Card className="card-subtle">
          <CardContent className="p-0">
            <table className="table-subtle" data-testid="storage-table">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  <SortableHeader field="name" className="text-left">VOLUMEN</SortableHeader>
                  <SortableHeader field="storage_type" className="text-left">TIPO</SortableHeader>
                  <SortableHeader field="capacity_gb" className="text-left">CAPACIDAD</SortableHeader>
                  <SortableHeader field="used_pct" className="text-left w-48">USO</SortableHeader>
                  <SortableHeader field="vm_count" className="text-center justify-center">VMs</SortableHeader>
                  <th className="text-center">COMPARTIDO</th>
                  <SortableHeader field="host_hostname" className="text-left">HOST</SortableHeader>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center p-8 text-muted-foreground">Cargando...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={7} className="text-center p-8 text-muted-foreground">No hay volúmenes registrados</td></tr>
                ) : items.map(st => (
                  <tr key={st.id} className="border-b hover:bg-muted/30" data-testid={`storage-row-${st.id}`}>
                    <td className="p-4"><div className="flex items-center gap-2"><HardDrive className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{st.name}</span></div></td>
                    <td className="p-4"><Badge variant="outline">{st.storage_type || '-'}</Badge></td>
                    <td className="p-4"><div><p className="text-sm">{formatSize(st.capacity_gb)}</p><p className="text-xs text-muted-foreground">Libre: {formatSize(st.free_gb)}</p></div></td>
                    <td className="p-4">
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs"><span>{Number(st.used_pct || 0).toFixed(1)}%</span></div>
                        <div className="h-2 bg-muted rounded overflow-hidden"><div className={`h-full ${getUsageColor(st.used_pct)}`} style={{ width: `${Math.min(Number(st.used_pct || 0), 100)}%` }} /></div>
                      </div>
                    </td>
                    <td className="p-4 text-center">{st.vm_count || 0}</td>
                    <td className="p-4 text-center">{st.is_cluster_shared ? <Share2 className="h-4 w-4 text-blue-500 inline" /> : <span className="text-muted-foreground">-</span>}</td>
                    <td className="p-4 text-sm text-muted-foreground"><div className="flex items-center gap-1"><Server className="h-3 w-3" />{st.host_hostname || '-'}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {pagination.pages > 1 && (
          <div className="flex justify-center items-center gap-4">
            <Button variant="outline" size="sm" disabled={pagination.page === 1} onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm text-muted-foreground">Página {pagination.page} de {pagination.pages} ({pagination.total} volúmenes)</span>
            <Button variant="outline" size="sm" disabled={pagination.page === pagination.pages} onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default HypervStorage;
